import DispatchContent from "../../features/planning/dailyDispatch/DispatchContent";
import JobReport from "../../features/planning/dailyDispatch/DailyJobReport/JobReport";
import DispatchScopeActionMenu from "../../features/planning/dailyDispatch/shared/DispatchScopeActionMenu";
import DeleteScopeBlockModal from "../../features/planning/shared/DeleteScopeBlockModal";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import DatesBar from "../../features/planning/dailyDispatch/DatesBar";
import TopBar from "../../features/planning/dailyDispatch/TopBar";

import WorkerSelectModal from "../../features/planning/shared/WorkerSelectModal";
import { Alert, ConfirmModal, Spinner } from "../../components/ui";

import {
  DispatchProvider,
  useDispatchContext,
} from "../../context/domain/planning/DispatchContext";
import useCaptureCtx, { CaptureContext } from "../../context/domain/planning/CaptureContext";
import { usePlanningContext } from "../../context/domain/planning/PlanningContext";
import useWeeklyCtx, { WeeklyContext, useWeeklyContext } from "../../context/global/WeeklyContext";
import {
  formatShortMonthDay,
  getDateInLocalTZFromDateStr,
  getDateString,
} from "../../utils/datetime/core";
import { getDispatchOverlayAlignForDate } from "../../features/planning/dailyDispatch/dispatchOverlayAlign";

import { handleKeyPress } from "../../utils/dom";

/**
 * Off-screen copy of the dispatch board. Playwright opens this page for both
 * the Complete Dispatch button and the 1:00 AM cron.
 *
 * @param {Object} props
 * @param {boolean} props.ready Whether week jobs, scopes, and day details have loaded.
 * @param {string} props.weekStartLabel Week start as YYYY-MM-DD.
 * @param {string} props.capturedOnDate Locale date printed in the capture header.
 * @param {string} props.capturedAtTime Locale time printed in the capture header.
 * @returns {JSX.Element}
 */
const CaptureBoard = ({
  ready,
  weekStartLabel,
  capturedOnDate,
  capturedAtTime,
}) => {
  return (
    <div
      className="dispatch-capture-root"
      data-dispatch-capture-ready={ready ? "true" : "false"}
      aria-hidden="true"
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        width: "1428px",
        zIndex: -1,
        backgroundColor: "white",
      }}
    >
      <div className="text-center font-bold font-inter text-3xl mt-4 mb-12">
        <p>R&S Dispatch Capture for Week Starting {weekStartLabel}</p>
        <p>
          Captured on {capturedOnDate} at {capturedAtTime}
        </p>
      </div>
      <DispatchContent className="dispatch-content-to-capture" />
    </div>
  );
};

/**
 * Inner component that uses both Weekly and Dispatch contexts.
 * This must be inside the WeeklyContext and DispatchProvider.
 *
 * @param {Object} props
 * @param {boolean} [props.compact=false] Whether compact chrome is enabled.
 * @param {boolean} [props.embedded=false] Only affects title bar background (and document.title).
 * @param {string} [props.title="R&S Dispatch"] Page heading.
 * @returns {JSX.Element}
 */
const DispatchInner = ({
  compact = false,
  embedded = false,
  title = "R&S Dispatch",
}) => {
  const { jobs, scopes, weekStart, showWeekends, datesOfWeek } = useWeeklyContext();
  const [searchParams] = useSearchParams();
  // Playwright opens this URL with ?capture=1. The button does not set the flag.
  const captureMode = searchParams.get("capture") === "1";
  const [captureStamp] = useState(() => ({
    date: new Date().toLocaleDateString(),
    time: new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }),
  }));
  const { planningLastUpdated } = usePlanningContext();
  const {
    isWorkerModalOpen,
    closeWorkerModal,
    modalProps,
    isDispatchCapturing,
    setIsDispatchCapturing,
    isJobReportCapturing,
    dayDetails,
    setCopyScopeOrigin,
    setCopyScopesDate,
    workerUnskipConflictModal,
    confirmWorkerUnskipConflictModal,
    cancelWorkerUnskipConflictModal,
    scopeActionMenu,
    compact: contextCompact,
    isLoading,
    deleteScopeBlockModal,
    deleteScopeBlockBusy,
    closeDeleteScopeBlockModal,
    confirmDeleteScopeBlock,
  } = useDispatchContext();
  const isCompact = compact || contextCompact;
  // Capture context is used for setting the state and vars of the capture. The capture
  // has different requirements than the main dispatch so it has its own context.
  const captureCtx = useCaptureCtx();

  useEffect(() => {
    if (!embedded) {
      document.title = title;
    }
  }, [embedded, title]);

  useEffect(() => {
    if (!captureMode) {
      return;
    }
    // Mount the capture board once the cron or the button opens ?capture=1.
    setIsDispatchCapturing(true);
  }, [captureMode, setIsDispatchCapturing]);

  const captureReady = Boolean(
    captureMode && jobs && scopes && dayDetails && !isLoading
  );

  useEffect(() => {
    const cleanup = handleKeyPress("Escape", () => {
      setCopyScopeOrigin(null);
      setCopyScopesDate(null);
    });

    return cleanup;
  }, [setCopyScopeOrigin, setCopyScopesDate]);

  // Title bg is the only layout difference for `embedded` (title text is a prop).
  const titleRow = (
    <div
      className="flex items-center justify-between gap-4 px-4 py-2 bg-gray-200"
    >
      <h1 className="text-2xl font-bold text-gray-950">{title}</h1>
      <div className="relative min-w-0">
        {isLoading ? (
          <span
            className="absolute right-full top-1/2 mr-2 flex -translate-y-1/2"
            aria-label="Loading week"
          >
            <Spinner
              variant="oval"
              size="h-4 w-4"
              color="#4fa94d"
              secondaryColor="#4fa94d"
              strokeWidth={4}
              strokeWidthSecondary={4}
            />
          </span>
        ) : null}
        <div className="truncate text-right text-xs text-gray-600">
          {planningLastUpdated}
        </div>
      </div>
    </div>
  );

  // TopBar + DatesBar always sit above content (full-width stacked DatesBar).
  // Sticky under NavBar on the full page; sticky at card top when compact.
  const dispatchBody = (
    <>
      {titleRow}
      <div className={`z-20 ${isCompact ? "sticky top-0" : "sticky top-14"}`}>
        <TopBar />
        <DatesBar layout="stacked" />
      </div>
      <DispatchContent hideDatesBar />
    </>
  );

  // Interaction modals are shared by full and compact embeds (crew edits, conflicts, etc.).
  const interactionModals = (
    <>
      {isWorkerModalOpen && (
        <WorkerSelectModal
          {...modalProps}
          isOpen={isWorkerModalOpen}
          onClose={closeWorkerModal}
        />
      )}
      {scopeActionMenu ? (
        <div
          className="fixed z-[10030] h-0 w-0"
          style={{
            left: scopeActionMenu.anchorRect?.right ?? 0,
            top: scopeActionMenu.anchorRect?.top ?? 0,
          }}
        >
          <DispatchScopeActionMenu
            scope={scopeActionMenu.scope}
            date={scopeActionMenu.date}
            align={
              getDispatchOverlayAlignForDate(
                scopeActionMenu.date,
                datesOfWeek
              ).menuAlign
            }
            showButton={false}
            openKey={scopeActionMenu.openKey}
            disableCopy={scopeActionMenu.disableCopy}
          />
        </div>
      ) : null}
      <DeleteScopeBlockModal
        open={Boolean(deleteScopeBlockModal)}
        block={deleteScopeBlockModal}
        busy={deleteScopeBlockBusy}
        onClose={closeDeleteScopeBlockModal}
        onConfirm={confirmDeleteScopeBlock}
      />
      <ConfirmModal
        isOpen={workerUnskipConflictModal != null}
        title="Worker Conflict Detected"
        buttonText="Confirm"
        onConfirm={confirmWorkerUnskipConflictModal}
        onClose={cancelWorkerUnskipConflictModal}
      >
        <div className="mx-auto max-w-md space-y-2 text-left">
          {(workerUnskipConflictModal ?? []).map(({ dateKey, conflictNames }) => (
            <Alert
              key={dateKey}
              type="warning"
              message={`${formatShortMonthDay(
                getDateInLocalTZFromDateStr(dateKey)
              )}: ${conflictNames.join(", ")}`}
            />
          ))}
          <div className="pt-2 text-center text-gray-600">
            This unskipped scope will be assigned an updated crew without workers
            already assigned or marked off work for this day.
          </div>
        </div>
      </ConfirmModal>
    </>
  );

  const weekMinWidthClass = showWeekends
    ? "min-w-dispatchWeekends"
    : "min-w-dispatch";

  // Compact (project embed): fill the card. Full page: min-height shell + capture clones.
  if (isCompact) {
    return (
      <>
        <div
          className={`font-inter w-full cursor-default caret-transparent bg-white ${weekMinWidthClass}`}
        >
          <div className="flex w-full flex-col">{dispatchBody}</div>
        </div>
        {interactionModals}
      </>
    );
  }

  return (
    <div
      className={`font-inter min-h-screen w-full cursor-default caret-transparent bg-custom-rsbg ${weekMinWidthClass}`}
    >
      <div className="flex min-h-screen w-full flex-col">{dispatchBody}</div>
      <CaptureContext.Provider value={captureCtx}>
        {isDispatchCapturing && captureMode && (
          <CaptureBoard
            ready={captureReady}
            weekStartLabel={weekStart ? getDateString(weekStart) : ""}
            capturedOnDate={captureStamp.date}
            capturedAtTime={captureStamp.time}
          />
        )}
        {isJobReportCapturing &&
          jobs &&
          jobs.map((job) => (
            <JobReport
              key={job.id}
              className={`${job.number} jobreport-content-to-capture`}
              job={job}
            />
          ))}
      </CaptureContext.Provider>
      {interactionModals}
    </div>
  );
};

/**
 * Entry point for the Daily Dispatch page and Project embeds.
 *
 * @component
 * @param {Object} [props]
 * @param {boolean} [props.compact=false] Whether to use compact dispatch chrome.
 * @param {boolean} [props.readOnly=false] Whether mutation UI/actions are disabled.
 * @param {Object|null} [props.singleProject=null] Optional project used to scope dispatch data.
 * @param {boolean} [props.detailsOnly=false] Whether to lock jobs into details view.
 * @param {boolean} [props.loadDayDetails] Whether to load DayDetails (locks/stages). Defaults true.
 * @param {boolean} [props.loadWeather] Whether to load weather. Defaults to !compact.
 * @param {Date|null} [props.initialDate=null] Optional week-seed date for embeds.
 * @param {boolean} [props.disableUrlSync=false] Whether week navigation updates local state only.
 * @param {number|null} [props.startOfWeek=null] Optional week-start day override.
 * @param {boolean} [props.embedded=false] Only affects title bar background (and document.title).
 * @param {string} [props.title="R&S Dispatch"] Page heading; override when embedding.
 * @returns {JSX.Element}
 */
const DailyDispatch = ({
  compact = false,
  readOnly = false,
  singleProject = null,
  detailsOnly = false,
  loadDayDetails,
  loadWeather,
  initialDate = null,
  disableUrlSync = false,
  startOfWeek = null,
  embedded = false,
  title = "R&S Dispatch",
} = {}) => {
  const weeklyCtx = useWeeklyCtx({
    initialDate,
    disableUrlSync,
    startOfWeek,
    blankUrlUsesWeekStart: true,
  });

  return (
    <WeeklyContext.Provider value={weeklyCtx}>
      <DispatchProvider
        compact={compact}
        readOnly={readOnly}
        singleProject={singleProject}
        detailsOnly={detailsOnly}
        options={{
          ...(loadDayDetails === undefined ? {} : { loadDayDetails }),
          ...(loadWeather === undefined ? {} : { loadWeather }),
        }}
      >
        <DispatchInner
          compact={compact}
          embedded={embedded}
          title={title}
        />
      </DispatchProvider>
    </WeeklyContext.Provider>
  );
};

export default DailyDispatch;
